import {
  ArchiveBoxArrowDownIcon,
  BriefcaseIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  InformationCircleIcon,
  MegaphoneIcon,
  PlusIcon,
  ShieldCheckIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { WAPI_DISCONNECTION_LINKS } from 'src/alerts/WapiDisconnection.copy';
import {
  WHATSAPP_BUSINESS_NO_WABA_IN_TOKEN,
  WHATSAPP_BUSINESS_WABA_IN_USE_BY_ANOTHER_WORKSPACE,
  WHATSAPP_SDK_TIMEOUT_ERROR_CODE,
} from 'src/constants/whatsappBusinessErrorCodes.constants';
import type { tEmbeddedSignupPath } from 'src/lib/facebook/embeddedSignupLogin';
import type { tPostConnectionPhase } from 'src/hooks/whatsapp/whatsappConnect.types';
import type { tWapiConnectionFlow } from 'src/hooks/analytics/wapiAnalytics.constants';
import type {
  tWizardActivationFailureReasonKey,
  tWizardAsideRow,
  tWizardErrorCatalogEntry,
  tWizardMetaGuideStep,
  tWizardMetaStepKey,
  tWizardPathCard,
} from './WhatsAppConnectionWizard.types';

/** The wizard's copy lives in the shared WhatsApp connection namespace. */
export const WIZARD_TRANSLATION_NAMESPACE = 'whatsappConnect/whatsappConnect';

/** Size and color of the neutral icons beside the card bullets and aside rows. */
export const WIZARD_ICON_CLASS_NAME =
  'mt-0.5 h-5 w-5 shrink-0 text-[var(--ls-text-neutral-light)]';

/** The title of a connection state screen, the main pane's one bold heading. */
export const WIZARD_SCREEN_TITLE_CLASS_NAME =
  'text-[length:var(--ls-h5)] font-bold text-[var(--ls-text-neutral-default)]';

/** The subtitle under a connection state screen's title. */
export const WIZARD_SCREEN_SUBTITLE_CLASS_NAME =
  'text-[length:var(--ls-body-lg)] text-[var(--ls-text-neutral-light)]';

/** Body copy of the connection state screens and the aside. */
export const WIZARD_BODY_CLASS_NAME =
  'text-[length:var(--ls-body)] text-[var(--ls-text-neutral-default)]';

/** Secondary lines, such as the code to mention to an advisor. */
export const WIZARD_CAPTION_CLASS_NAME =
  'text-[length:var(--ls-caption)] text-[var(--ls-text-neutral-light)]';

/** A section title of the aside. */
export const WIZARD_ASIDE_TITLE_CLASS_NAME =
  'text-[length:var(--ls-subtitle)] font-bold text-[var(--ls-text-neutral-default)]';

/**
 * The YouTube walkthrough of the connection. It is the video the WAPI
 * disconnection notices link to, so both always point to the same one.
 */
export const WIZARD_CONNECTION_VIDEO_URL =
  WAPI_DISCONNECTION_LINKS.VIDEO_COEXISTENCIA;

/** Tailwind's `md` breakpoint, where the wizard's panes sit side by side. */
export const WIZARD_DESKTOP_MEDIA_QUERY = '(min-width: 768px)';

/**
 * Minutes after Meta's activation decision at which WBS escalates a held
 * number to support (`ACTIVATION_ESCALATION_MINUTES`, its default), and the
 * pending screen makes the advisor its primary action.
 */
export const WIZARD_ACTIVATION_ESCALATION_MINUTES = 60;

/** When the activating screen shows its indicator, per the DS loading rule. */
export const WIZARD_ACTIVATING_INDICATOR_DELAY_MILLISECONDS = 400;

/** When the activating screen shows its loading text and steps. */
export const WIZARD_ACTIVATING_TEXT_DELAY_MILLISECONDS = 3_000;

/** The OS setting under which the brand moment shows its final state, still. */
export const WIZARD_REDUCED_MOTION_MEDIA_QUERY =
  '(prefers-reduced-motion: reduce)';

/**
 * How long the connected screen's brand moment plays: `--ls-duration-xl`,
 * the one movement of the mark.
 */
export const WIZARD_BRAND_MOMENT_DURATION_MILLISECONDS = 400;

/**
 * Meta's history sync `error_code` when the person did not share the
 * history; final for that connection.
 */
export const WIZARD_HISTORY_NOT_SHARED_ERROR_CODE = 2593109;

/**
 * The exchange error codes that name their cause, so their screen keeps the
 * copy of that cause. Any other failed exchange carries no verdict.
 */
export const WIZARD_EXCHANGE_ERROR_CODES_WITH_VERDICT: ReadonlySet<string> =
  new Set([
    WHATSAPP_BUSINESS_NO_WABA_IN_TOKEN,
    WHATSAPP_BUSINESS_WABA_IN_USE_BY_ANOTHER_WORKSPACE,
    WHATSAPP_SDK_TIMEOUT_ERROR_CODE,
  ]);

/**
 * The activation failure reasons with their own copy. A Map, so a reason that
 * names an object property is never matched.
 */
export const WIZARD_ACTIVATION_FAILURE_REASON_KEYS = new Map<
  string,
  tWizardActivationFailureReasonKey
>([
  ['cloud_api_account_missing', 'notActivated'],
  ['waba_not_visible', 'lostAccess'],
]);

/** The `connection_flow` of every connection event the wizard sends. */
export const WIZARD_CONNECTION_FLOW: tWapiConnectionFlow = 'wizard';

/** Steps of the flow: choose the path, connect in Meta, activation result. */
export const WIZARD_STEP_COUNT = 3;

/**
 * The step each connection phase belongs to. Failures inside Meta stay on the
 * Meta step; a failed exchange happens after Meta handed over its code, so it
 * belongs to the activation step with the held, rejected and connected
 * verdicts. `skipped` comes from onboarding and can be restored from storage
 * here, so it opens on the path choice like `connect`.
 */
export const WIZARD_STEP_BY_PHASE: Record<tPostConnectionPhase, number> = {
  connect: 1,
  skipped: 1,
  connecting: 2,
  failed_error: 3,
  failed_closed: 2,
  escalated: 2,
  verifying: 3,
  activation_failed: 3,
  success: 3,
};

/**
 * Step 1 answers, in the order they are shown. Neither is selected when the
 * wizard opens: the person picks the path on purpose.
 */
export const WIZARD_PATH_CARDS: tWizardPathCard[] = [
  {
    path: 'cloud_api',
    nameKey: 'wizard.paths.cloudApi.name',
    answerKey: 'wizard.paths.cloudApi.answer',
    keepsApp: false,
    gain: { textKey: 'wizard.paths.cloudApi.gain', icon: PlusIcon },
    watch: {
      textKey: 'wizard.paths.cloudApi.watch',
      icon: InformationCircleIcon,
    },
    chosenBlurbKey: 'wizard.paths.cloudApi.chosenBlurb',
  },
  {
    path: 'coexistence',
    nameKey: 'wizard.paths.coexistence.name',
    answerKey: 'wizard.paths.coexistence.answer',
    keepsApp: true,
    gain: { textKey: 'wizard.paths.coexistence.gain', icon: PlusIcon },
    watch: {
      textKey: 'wizard.paths.coexistence.watch',
      icon: InformationCircleIcon,
    },
    chosenBlurbKey: 'wizard.paths.coexistence.chosenBlurb',
  },
];

/** The "Ten a la mano" rows every path shares, before the path's own rows. */
export const WIZARD_REQUIREMENT_ROWS: tWizardAsideRow[] = [
  {
    textKey: 'wizard.aside.requirements.facebookProfile',
    icon: UserCircleIcon,
  },
  {
    textKey: 'wizard.aside.requirements.metaPortfolio',
    icon: BriefcaseIcon,
  },
];

/**
 * The last "Ten a la mano" row while no answer is chosen: the phone serves
 * both paths, to scan a QR code or to get a code.
 */
export const WIZARD_GENERAL_PHONE_REQUIREMENT_ROW: tWizardAsideRow = {
  textKey: 'wizard.aside.requirements.phoneGeneral',
  icon: DevicePhoneMobileIcon,
};

/**
 * The last "Ten a la mano" rows of each path: Coexistencia scans a QR code
 * with the phone that runs WhatsApp Business; Cloud API needs only a phone
 * that gets that number's messages or calls, and a backup of the app's
 * conversations, which moving the number off the app deletes.
 */
export const WIZARD_PATH_REQUIREMENT_ROWS: Record<
  tEmbeddedSignupPath,
  tWizardAsideRow[]
> = {
  coexistence: [
    {
      textKey: 'wizard.aside.requirements.phoneCoexistence',
      icon: DevicePhoneMobileIcon,
    },
  ],
  cloud_api: [
    {
      textKey: 'wizard.aside.requirements.phoneCloudApi',
      icon: DevicePhoneMobileIcon,
    },
    {
      textKey: 'wizard.aside.requirements.conversationsBackup',
      icon: ArchiveBoxArrowDownIcon,
    },
  ],
};

/**
 * The "Después de conectar" rows of each path. Only Coexistencia has them:
 * the app's new terms, the closed WhatsApp Web sessions and the read-only
 * broadcast lists happen only on that path.
 */
export const WIZARD_AFTER_CONNECTING_ROWS: Record<
  tEmbeddedSignupPath,
  tWizardAsideRow[]
> = {
  coexistence: [
    {
      textKey: 'wizard.aside.coexistence.acceptTerms',
      icon: ShieldCheckIcon,
    },
    {
      textKey: 'wizard.aside.coexistence.webSessions',
      icon: ComputerDesktopIcon,
    },
    {
      textKey: 'wizard.aside.coexistence.broadcastLists',
      icon: MegaphoneIcon,
    },
  ],
  cloud_api: [],
};

/** The waiting screen's title, which names the path Meta opened on. */
export const WIZARD_WAITING_TITLE_KEYS: Record<tEmbeddedSignupPath, string> = {
  coexistence: 'wizard.waiting.titleCoexistence',
  cloud_api: 'wizard.waiting.titleCloudApi',
};

/**
 * The Meta screen each `current_step` of a close names. Matched by Meta's
 * value, never by the step's position, since each path orders Meta's screens
 * differently. A step not listed closes with no step named.
 */
export const WIZARD_META_STEP_BY_SIGNUP_STEP: ReadonlyMap<
  string,
  tWizardMetaStepKey
> = new Map<string, tWizardMetaStepKey>([
  ['ASSET_SELECTION', 'portfolio'],
  ['ASSET_CREATION', 'portfolio'],
  ['WHATSAPP_BUSINESS_APP_ONBOARDING_QR_CODE', 'qrCode'],
  ['WHATSAPP_BUSINESS_APP_ONBOARDING_PHONE_CREATION', 'phoneChoice'],
  ['PHONE_NUMBER_SETUP', 'businessNumber'],
  ['PHONE_NUMBER_VERIFICATION', 'verificationCode'],
]);

/** The address in the title bar of the built Meta views. */
export const META_VIEW_ADDRESS = 'business.facebook.com';

/** Where the captures of Meta's screens are served from. */
const META_SCREENSHOT_DIRECTORY = '/images/whatsappConnect/meta/';

const CONFIRM_ACCOUNT_STEP: tWizardMetaGuideStep = {
  key: 'confirmAccount',
  view: [
    { kind: 'title', textKey: 'confirmAccountTitle' },
    { kind: 'button', textKey: 'cancel' },
  ],
  highlighted: [{ kind: 'button', textKey: 'continueAs', isEmphasized: true }],
  screenshot: {
    src: `${META_SCREENSHOT_DIRECTORY}confirma-cuenta.png`,
    width: 960,
    height: 700,
    capturedOn: '2026-10-07',
    mark: [49.5, 74.5, 49.5, 12],
  },
};

const PORTFOLIO_STEP: tWizardMetaGuideStep = {
  key: 'portfolio',
  hasNote: true,
  view: [
    { kind: 'title', textKey: 'portfolioTitle' },
    { kind: 'caption', textKey: 'portfolioCaption' },
  ],
  highlighted: [
    { kind: 'label', textKey: 'portfolioLabel' },
    { kind: 'field', textKey: 'portfolioName' },
  ],
  viewAfter: [
    { kind: 'label', textKey: 'whatsAppAccountLabel' },
    { kind: 'field', textKey: 'whatsAppAccountName' },
  ],
};

// Meta's window does not close by itself when it confirms, so the capture
// marks "Finalizar"; the built view marks nothing.
const META_CONFIRMS_STEP: tWizardMetaGuideStep = {
  key: 'metaConfirms',
  view: [
    { kind: 'title', textKey: 'connectedTitle' },
    { kind: 'button', textKey: 'finish', isEmphasized: true },
  ],
  screenshot: {
    src: `${META_SCREENSHOT_DIRECTORY}meta-confirma.png`,
    width: 1124,
    height: 1165,
    capturedOn: '2026-10-22',
    mark: [81, 91.5, 17, 7.5],
  },
};

/**
 * The Meta screens of each path, in the order Meta shows them: Coexistencia
 * asks for the number before the portfolio. A step with a screenshot shows
 * Meta's capture; each built view holds only the parts of Meta's screen the
 * hint names, with the texts under `wizard.metaGuide.view`. The portfolio
 * screen differs per path, so each path has its own capture of it.
 */
export const WIZARD_META_GUIDE_STEPS: Record<
  tEmbeddedSignupPath,
  tWizardMetaGuideStep[]
> = {
  coexistence: [
    CONFIRM_ACCOUNT_STEP,
    {
      key: 'phoneChoice',
      view: [{ kind: 'title', textKey: 'addNumberTitle' }],
      highlighted: [
        { kind: 'radio', textKey: 'appNumber', isEmphasized: true },
      ],
      viewAfter: [{ kind: 'radio', textKey: 'otherNumber' }],
      screenshot: {
        src: `${META_SCREENSHOT_DIRECTORY}elige-numero.png`,
        width: 990,
        height: 752,
        capturedOn: '2026-10-22',
        mark: [3.5, 63.5, 92, 11.5],
      },
    },
    {
      ...PORTFOLIO_STEP,
      screenshot: {
        src: `${META_SCREENSHOT_DIRECTORY}portafolio-coexistencia.png`,
        width: 980,
        height: 310,
        capturedOn: '2026-10-07',
        mark: [1, 50.5, 98.5, 45],
      },
    },
    {
      key: 'qrCode',
      hasNote: true,
      view: [{ kind: 'title', textKey: 'importChatsTitle' }],
      highlighted: [{ kind: 'qr' }],
      screenshot: {
        src: `${META_SCREENSHOT_DIRECTORY}codigo-qr.png`,
        width: 1000,
        height: 830,
        capturedOn: '2026-10-22',
        mark: [20, 25.5, 60.5, 72.5],
      },
    },
    META_CONFIRMS_STEP,
  ],
  cloud_api: [
    CONFIRM_ACCOUNT_STEP,
    {
      ...PORTFOLIO_STEP,
      screenshot: {
        src: `${META_SCREENSHOT_DIRECTORY}portafolio-cloud-api.png`,
        width: 980,
        height: 450,
        capturedOn: '2026-10-07',
        mark: [1, 35, 98.5, 31],
      },
    },
    {
      key: 'businessNumber',
      hasNote: true,
      view: [{ kind: 'title', textKey: 'addNumberTitle' }],
      highlighted: [
        { kind: 'radio', textKey: 'newNumber', isEmphasized: true },
        { kind: 'field', textKey: 'appNumber' },
      ],
      viewAfter: [
        { kind: 'radio', textKey: 'ineligibleNumber' },
        { kind: 'label', textKey: 'verifyMethodLabel' },
        { kind: 'radio', textKey: 'textMessage', isEmphasized: true },
        { kind: 'radio', textKey: 'phoneCall' },
      ],
      screenshot: {
        src: `${META_SCREENSHOT_DIRECTORY}numero-cloud-api.png`,
        width: 980,
        height: 480,
        capturedOn: '2026-10-07',
        mark: [1, 32, 98.5, 38],
      },
    },
    {
      key: 'verificationCode',
      hasNote: true,
      view: [{ kind: 'title', textKey: 'confirmationCodeTitle' }],
      highlighted: [{ kind: 'field', textKey: 'sampleCode' }],
    },
    META_CONFIRMS_STEP,
  ],
};

/**
 * Where business verification starts in Meta. Without a portfolio id, Meta
 * opens the portfolio the person used last.
 */
export const META_SECURITY_CENTER_URL =
  'https://business.facebook.com/latest/settings/security_center';

/** Where Meta shows a restricted account and lets the person ask for a review. */
const META_BUSINESS_SUPPORT_HOME_URL =
  'https://business.facebook.com/business-support-home';

/** Where a business admin gives people access to its WhatsApp accounts. */
const META_PEOPLE_SETTINGS_URL =
  'https://business.facebook.com/settings/people';

/**
 * The Meta errors the wizard explains. Their copy lives under
 * `wizard.errorCatalog.<id>`. Business verification has no known Meta code, so
 * it is listed for reference and never matched from a report: matching Meta's
 * text could show a wrong cause.
 */
export const WIZARD_ERROR_CATALOG: tWizardErrorCatalogEntry[] = [
  {
    id: 'businessVerification',
    metaErrorCode: null,
    whoActs: 'user',
    stepCount: 4,
    footerPrimary: 'openInMeta',
    retry: {
      presentation: 'ghost',
      labelKey: 'wizard.errorCatalog.businessVerification.retryLabel',
    },
    metaUrl: META_SECURITY_CENTER_URL,
  },
  {
    id: '2655093',
    metaErrorCode: '2655093',
    whoActs: 'advisor',
    stepCount: 3,
    footerPrimary: 'advisor',
    retry: {
      presentation: 'ghost',
      labelKey: 'wizard.errorCatalog.2655093.retryLabel',
    },
  },
  {
    id: '2655123',
    metaErrorCode: '2655123',
    whoActs: 'user',
    stepCount: 3,
    footerPrimary: 'retry',
    retry: { presentation: 'primary' },
  },
  {
    id: '3441045',
    metaErrorCode: '3441045',
    whoActs: 'advisor',
    stepCount: 3,
    footerPrimary: 'advisor',
    retry: {
      presentation: 'ghost',
      labelKey: 'wizard.errorCatalog.3441045.retryLabel',
    },
  },
  {
    id: '2655121',
    metaErrorCode: '2655121',
    whoActs: 'user',
    stepCount: 3,
    footerPrimary: 'openInMeta',
    retry: {
      presentation: 'ghost',
      labelKey: 'wizard.errorCatalog.2655121.retryLabel',
    },
    metaUrl: META_BUSINESS_SUPPORT_HOME_URL,
  },
  {
    id: '4563004',
    metaErrorCode: '4563004',
    whoActs: 'user',
    stepCount: 1,
    footerPrimary: 'retry',
    retry: { presentation: 'primary' },
  },
  {
    id: '3441034',
    metaErrorCode: '3441034',
    whoActs: 'advisor',
    stepCount: 2,
    footerPrimary: 'advisor',
    retry: {
      presentation: 'ghost',
      labelKey: 'wizard.errorCatalog.3441034.retryLabel',
    },
  },
  {
    id: '3441038',
    metaErrorCode: '3441038',
    whoActs: 'user',
    stepCount: 2,
    footerPrimary: 'openInMeta',
    retry: {
      presentation: 'ghost',
      labelKey: 'wizard.errorCatalog.3441038.retryLabel',
    },
    metaUrl: META_PEOPLE_SETTINGS_URL,
  },
  // The fix is in the WhatsApp Business app on the phone, so no Meta link.
  {
    id: '3441061',
    metaErrorCode: '3441061',
    whoActs: 'user',
    stepCount: 4,
    footerPrimary: 'retry',
    retry: { presentation: 'primary' },
  },
  {
    id: '1675012',
    metaErrorCode: '1675012',
    whoActs: 'user',
    stepCount: 2,
    footerPrimary: 'retry',
    retry: { presentation: 'primary' },
  },
  {
    id: '2388091',
    metaErrorCode: '2388091',
    whoActs: 'advisor',
    stepCount: 3,
    footerPrimary: 'advisor',
    retry: {
      presentation: 'ghost',
      labelKey: 'wizard.errorCatalog.2388091.retryLabel',
    },
  },
  {
    id: '2494064',
    metaErrorCode: '2494064',
    whoActs: 'advisor',
    stepCount: 3,
    footerPrimary: 'advisor',
    retry: {
      presentation: 'ghost',
      labelKey: 'wizard.errorCatalog.2494064.retryLabel',
    },
  },
];
